import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  Link,
  Text,
  useToast,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetAreMasterLoanTapesAssignedQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import { AssignmentLoadingSkeleton } from 'packages/loanProduct/ui/AssignmentSkeletonLoadingTables';
import { AssignmentTemplate } from 'packages/loanProduct/ui/AssignmentTemplate';
import { AiFillCheckCircle } from 'react-icons/ai';

export const AreMasterLoanTapesAssignedCheckContainer = ({
  masterLoanTapeIds,
  fileVersion,
  reportingDate,
  children,
}: {
  masterLoanTapeIds: string[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
  children: React.ReactNode;
}) => {
  const toast = useToast();
  const {
    data: areMasterLoanTapesAssignedData,
    loading: areMasterLoanTapesAssignedLoading,
  } = useGetAreMasterLoanTapesAssignedQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      masterLoanTapeIds,
    },
    fetchPolicy: 'no-cache',
  });

  if (!areMasterLoanTapesAssignedData || areMasterLoanTapesAssignedLoading) {
    return (
      <AssignmentLoadingSkeleton dataTestId="loading-are-master-loan-tapes-assigned-check-container" />
    );
  }

  const iconWidth = 28;
  const iconMargin = 12;

  if (areMasterLoanTapesAssignedData.areMasterLoanTapesAssigned) {
    return (
      <Box height="100%">
        <AssignmentTemplate>
          <Flex mt="30px" mb="10px" alignItems="center">
            <Grid
              templateRows="repeat(2, 1fr)"
              templateColumns="repeat(20, 1fr)"
            >
              <GridItem rowSpan={2} colSpan={1}>
                <Icon
                  color={colors.green[400]}
                  as={AiFillCheckCircle}
                  marginRight={`${iconMargin}px`}
                  fontSize={`${iconWidth}px`}
                />
              </GridItem>
              <GridItem colSpan={14}>
                <Text>
                  Your current master loan tapes have already been assigned for
                  the following reporting date and file version:
                </Text>
              </GridItem>
              <GridItem colSpan={14}>
                <Text>
                  Reporting date:{' '}
                  {commonDate.fullDateStrToHumanDateString(reportingDate)}
                </Text>
                <Text>File version: {fileVersion}</Text>
              </GridItem>
            </Grid>
          </Flex>
          <Flex mt="30px">
            <Text>
              To assign additional receivables, please upload new loan tapes on
              the{' '}
              <Link
                as={RouterLink}
                to="/data-pipeline"
                style={{ textDecoration: 'underline' }}
              >
                Data Pipeline
              </Link>{' '}
              page.
            </Text>
          </Flex>
        </AssignmentTemplate>
      </Box>
    );
  }

  return <Box height="100%">{children}</Box>;
};
